import React from 'react';
import { Typography, Button } from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CloudOffIcon from '@material-ui/icons/CloudOff';

/**
 * Dois banners renderizados condicionalmente dentro de admin-trial-detail e admin-subscription-detail:
 *
 * 1) Desprovisionamento agendado (amarelo):
 *    status === 'deprovisioning_scheduled' → mostra countdown até scheduled_at + botão "Abortar".
 *
 * 2) Database drop agendado (cinza):
 *    drop_status === 'pending' → mostra quanto tempo falta para o drop + botão "Abortar drop".
 *    Aparece em paralelo ao primeiro se o ambiente já foi desprovisionado e está na janela de retenção.
 *
 * Props:
 *  - status: objeto retornado por GET /admin/billing/hirers/{id}/deprovisioning-status
 *  - onAbortDeprovisioning: callback
 *  - onAbortDatabaseDrop: callback
 *  - aborting: bool — bloqueia botões durante requisição
 */
function formatCountdown(targetDate) {
    if (!targetDate) return '';
    const target = new Date(targetDate).getTime();
    const diff = target - Date.now();
    if (diff <= 0) return 'iniciando a qualquer momento';

    const totalMinutes = Math.floor(diff / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h restantes`;
    if (hours > 0) return `${hours}h ${minutes}min restantes`;
    return `${minutes}min restantes`;
}

function DeprovisioningBanner({ status, onAbortDeprovisioning, onAbortDatabaseDrop, aborting }) {
    if (!status) return null;

    const showScheduledBanner = status.provisioningstatus === 'deprovisioning_scheduled';
    const showDropBanner = status.dropstatus === 'pending' && status.dropscheduledat;

    if (!showScheduledBanner && !showDropBanner) return null;

    return (
        <div style={{ marginBottom: 16 }}>
            {showScheduledBanner && (
                <div style={{
                    padding: '14px 18px',
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffca28',
                    borderLeft: '4px solid #f57c00',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: showDropBanner ? 8 : 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <ScheduleIcon style={{ color: '#f57c00', marginTop: 2 }} />
                        <div>
                            <Typography variant="body1" style={{ fontWeight: 600, color: '#e65100' }}>
                                Desprovisionamento agendado
                            </Typography>
                            <Typography variant="body2" style={{ color: '#6d4c41', marginTop: 2 }}>
                                {formatCountdown(status.deprovisioningscheduledat)} — iniciado por{' '}
                                <strong>{status.deprovisioningrequestedby || 'sistema'}</strong>
                                {status.deprovisioningreason ? ` (${status.deprovisioningreason})` : ''}.
                                Após iniciar, não será possível abortar.
                            </Typography>
                        </div>
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onAbortDeprovisioning}
                        disabled={aborting}
                        style={{
                            borderColor: '#f57c00',
                            color: '#f57c00',
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Abortar desprovisionamento
                    </Button>
                </div>
            )}

            {showDropBanner && (
                <div style={{
                    padding: '14px 18px',
                    backgroundColor: '#eceff1',
                    border: '1px solid #b0bec5',
                    borderLeft: '4px solid #546e7a',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <CloudOffIcon style={{ color: '#546e7a', marginTop: 2 }} />
                        <div>
                            <Typography variant="body1" style={{ fontWeight: 600, color: '#37474f' }}>
                                Banco de dados será deletado {formatCountdown(status.dropscheduledat)}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#546e7a', marginTop: 2 }}>
                                O ambiente já foi desprovisionado. O banco de dados do contratante está preservado
                                até <strong>{new Date(status.dropscheduledat).toLocaleString('pt-BR')}</strong>.
                                Você pode abortar a deleção para manter o banco permanentemente.
                            </Typography>
                        </div>
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onAbortDatabaseDrop}
                        disabled={aborting}
                        style={{
                            borderColor: '#546e7a',
                            color: '#546e7a',
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Abortar deleção do banco de dados
                    </Button>
                </div>
            )}
        </div>
    );
}

export default DeprovisioningBanner;
